/**
 * <dropit-sampler> — the "Experimente" tasting kit (sections/dropit-sampler.liquid).
 *
 * - Tapping a coffee (its 18g variant) adds one dose to the kit; the card then shows a stepper and a badge.
 * - The kit lives in the side pre-cart (desktop) or in a bottom bar + sheet (below 990px).
 * - "Adicionar ao carrinho" sends the whole kit in one call; "Levar os N" sends one of every coffee.
 *   Both go through Shopify.actions.updateCart, so Horizon refreshes and opens the cart drawer by itself.
 * - The kit is kept in sessionStorage while the shopper browses ("Detalhes" opens the product page).
 */

const STORE_KEY = 'dropit:kit-v1';
const MAX_QTY = 10;

const money = (cents) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }).replace(/ /g, ' ');

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/**
 * @param {Array<{merchandiseId: string, quantity: number}>} lines
 */
async function addToCart(lines) {
  const actions = window.Shopify?.actions;
  if (actions?.updateCart) {
    const result = await actions.updateCart({ lines }, { event: { context: 'cart' } });
    if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
    actions.openCart?.();
    return;
  }

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: lines.map((line) => ({ id: line.merchandiseId, quantity: line.quantity })) }),
  });
  if (!response.ok) throw new Error('cart');
  window.location.href = '/cart';
}

class DropitSampler extends HTMLElement {
  /** @type {Map<string, {el: HTMLElement, id: string, name: string, meta: string, price: number, img: string, available: boolean}>} */
  items = new Map();

  /** @type {Record<string, number>} */
  qty = {};

  connectedCallback() {
    for (const el of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-kit-item]'))) {
      const { id = '', name = '', meta = '', price = '0', img = '' } = el.dataset;
      this.items.set(id, { el, id, name, meta, price: Number(price), img, available: !el.hasAttribute('data-soldout') });
    }

    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
      for (const [id, q] of Object.entries(saved)) {
        if (this.items.get(id)?.available && q > 0) this.qty[id] = Math.min(MAX_QTY, Number(q));
      }
    } catch {}

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-sheet')) this.#sheet(false);
    });
    this.#render();
  }

  #save() {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(this.qty));
    } catch {}
  }

  get #lines() {
    return Object.entries(this.qty)
      .filter(([, q]) => q > 0)
      .map(([id, q]) => ({ item: this.items.get(id), q }))
      .filter((line) => line.item);
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-act]') : null;
    if (!(target instanceof HTMLElement) || (target instanceof HTMLButtonElement && target.disabled)) return;
    const id = target.dataset.id || '';

    switch (target.dataset.act) {
      case 'inc':
        if (!this.items.get(id)?.available) return;
        this.qty[id] = Math.min(MAX_QTY, (this.qty[id] || 0) + 1);
        break;
      case 'dec':
        this.qty[id] = Math.max(0, (this.qty[id] || 0) - 1);
        if (!this.qty[id]) delete this.qty[id];
        break;
      case 'sheet':
        this.#sheet(true);
        return;
      case 'close':
        this.#sheet(false);
        return;
      case 'cart':
        await this.#send(
          target,
          this.#lines.map(({ item, q }) => ({ merchandiseId: item.id, quantity: q })),
          true
        );
        return;
      case 'all':
        await this.#send(
          target,
          [...this.items.values()].filter((item) => item.available).map((item) => ({ merchandiseId: item.id, quantity: 1 })),
          false
        );
        return;
      default:
        return;
    }

    this.#save();
    this.#render();
  };

  /**
   * @param {HTMLElement} button
   * @param {Array<{merchandiseId: string, quantity: number}>} lines
   * @param {boolean} clearKit
   */
  async #send(button, lines, clearKit) {
    if (!lines.length) return;
    button.setAttribute('aria-busy', 'true');
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.error = '';

    try {
      await addToCart(lines);
      if (clearKit) {
        this.qty = {};
        this.#save();
      }
      this.#sheet(false);
    } catch (error) {
      this.error = 'Não deu para adicionar ao carrinho. Tente de novo.';
    } finally {
      button.removeAttribute('aria-busy');
      if (button instanceof HTMLButtonElement) button.disabled = false;
      this.#render();
    }
  }

  /** @param {boolean} open */
  #sheet(open) {
    const sheet = /** @type {HTMLElement | null} */ (this.querySelector('[data-r="sheet"]'));
    if (!sheet) return;
    this.classList.toggle('is-sheet', open);
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      this.lastFocus = document.activeElement;
      sheet.focus({ preventScroll: true });
    } else if (this.lastFocus instanceof HTMLElement) {
      this.lastFocus.focus({ preventScroll: true });
      this.lastFocus = null;
    }
  }

  #render() {
    const lines = this.#lines;
    const count = lines.reduce((sum, { q }) => sum + q, 0);
    const total = lines.reduce((sum, { item, q }) => sum + item.price * q, 0);
    const countLabel = count === 1 ? '1 café' : `${count} cafés`;

    // Cards: badge, selected state and the add button / stepper
    for (const item of this.items.values()) {
      const q = this.qty[item.id] || 0;
      item.el.classList.toggle('is-in', q > 0);
      const badge = item.el.querySelector('[data-r="badge"]');
      if (badge) badge.textContent = q ? `${q}×` : '';
      const act = item.el.querySelector('[data-r="act"]');
      if (!act || !item.available) continue;

      const mode = q > 0 ? 'qty' : 'add';
      if (act.getAttribute('data-mode') !== mode) {
        act.setAttribute('data-mode', mode);
        act.innerHTML =
          mode === 'add'
            ? `<button type="button" class="dp-kit__add" data-act="inc" data-id="${item.id}"><span aria-hidden="true">+</span> Adicionar</button>`
            : `<div class="dp-kit__qty">
                <button type="button" data-act="dec" data-id="${item.id}" aria-label="Tirar uma dose de ${escapeHtml(item.name)}">−</button>
                <output aria-live="polite"></output>
                <button type="button" data-act="inc" data-id="${item.id}" aria-label="Mais uma dose de ${escapeHtml(item.name)}">+</button>
              </div>`;
      }
      const output = act.querySelector('output');
      if (output) output.textContent = String(q);
      const plus = /** @type {HTMLButtonElement | null} */ (act.querySelector('.dp-kit__qty [data-act="inc"]'));
      if (plus) plus.disabled = q >= MAX_QTY;
    }

    // Pre-cart body (desktop aside + sheet)
    const body = lines.length
      ? `<ul class="dp-kit__lines" role="list">${lines
          .map(
            ({ item, q }) => `<li class="dp-kit__line">
              ${item.img ? `<img src="${item.img}" alt="" width="44" height="44" loading="lazy">` : ''}
              <span class="dp-kit__line-name">${escapeHtml(item.name)}</span>
              <span class="dp-kit__line-qty">
                <button type="button" data-act="dec" data-id="${item.id}" aria-label="Tirar uma dose de ${escapeHtml(item.name)}">−</button>
                <output>${q}</output>
                <button type="button" data-act="inc" data-id="${item.id}" aria-label="Mais uma dose de ${escapeHtml(item.name)}"${q >= MAX_QTY ? ' disabled' : ''}>+</button>
              </span>
              <span class="dp-kit__line-price">${money(item.price * q)}</span>
            </li>`
          )
          .join('')}</ul>
          <p class="dp-kit__total">Total <b>${money(total)}</b></p>`
      : '<p class="dp-kit__empty">Seu kit está vazio. Toque nos cafés para adicionar uma dose de cada.</p>';

    const allCount = Number(this.dataset.allCount || 0);
    const allLabel = `Levar os ${allCount} · ${money(Number(this.dataset.allTotal || 0))}`;
    const foot = `<button type="button" class="dp-kit__cta" data-act="cart"${count ? '' : ' disabled'}>Adicionar ao carrinho <span aria-hidden="true">→</span></button>
      ${allCount > 1 ? `<button type="button" class="dp-kit__all" data-act="all">${allLabel}</button>` : ''}
      ${this.error ? `<p class="dp-kit__error" role="alert">${this.error}</p>` : ''}`;

    for (const slot of this.querySelectorAll('[data-r="body"]')) {
      slot.innerHTML = `<div class="dp-kit__body">${body}${foot}</div>`;
    }
    for (const slot of this.querySelectorAll('[data-r="count"]')) slot.textContent = count ? countLabel : '';

    // Bottom bar: always on; with an empty kit its button takes every coffee ("Levar os N")
    const bar = this.querySelector('[data-r="bar"]');
    if (bar) {
      bar.toggleAttribute('data-show', count > 0 || allCount > 1);
      bar.toggleAttribute('data-empty', count === 0);
      const sum = bar.querySelector('[data-r="bar-sum"]');
      if (sum) sum.textContent = count ? `${countLabel} · ${money(total)}` : 'Seu kit está vazio';
      const hint = bar.querySelector('[data-r="bar-hint"]');
      if (hint) hint.textContent = count ? 'Ver o kit' : 'Toque nos cafés para montar';
      const cta = /** @type {HTMLElement | null} */ (bar.querySelector('[data-r="bar-cta"]'));
      if (cta && !cta.hasAttribute('aria-busy')) {
        cta.dataset.act = count ? 'cart' : 'all';
        cta.textContent = count ? 'Adicionar' : `Levar os ${allCount}`;
      }
    }
    if (!count && this.classList.contains('is-sheet')) this.#sheet(false);
  }
}

if (!customElements.get('dropit-sampler')) customElements.define('dropit-sampler', DropitSampler);
